import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';
import { categories, getHotMovies, getNewMovies, getClassicMovies } from '@/mocks/movies';

const heroBackgrounds = [
  'https://readdy.ai/api/search-image?query=Epic%20cinematic%20movie%20collage%20background%20with%20dramatic%20lighting%2C%20film%20reels%2C%20theater%20curtains%2C%20abstract%20dark%20atmosphere%20with%20warm%20golden%20spotlight%20rays%2C%20cinematic%20texture%2C%20movie%20poster%20background%20style&width=1920&height=1080&seq=hero-bg-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Luxurious%20home%20theater%20interior%20with%20large%20screen%2C%20comfortable%20red%20seats%2C%20dim%20warm%20lighting%2C%20cinematic%20atmosphere%2C%20cozy%20movie%20night%20vibe%2C%20dark%20ambient&width=1920&height=1080&seq=hero-bg-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Abstract%20geometric%20streaming%20platform%20background%20with%20flowing%20light%20trails%2C%20dark%20theme%2C%20modern%20tech%20aesthetic%2C%20smooth%20gradients%2C%20cinematic%20vibe%2C%20minimalist%20and%20elegant&width=1920&height=1080&seq=hero-bg-03&orientation=landscape',
];

export default function Home() {
  const [currentBg, setCurrentBg] = useState(0);
  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();
  const classicMovies = getClassicMovies();

  const nextBg = useCallback(() => {
    setCurrentBg((prev) => (prev + 1) % heroBackgrounds.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBg, 5000);
    return () => clearInterval(timer);
  }, [nextBg]);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '大师兄影视-电视剧全集电影大全综艺节目热门剧集经典影片高清免费在线观看',
            description: '大师兄影视汇聚电视剧全集、电影大全、热门综艺、经典影片，高清免费在线观看，不缓冲秒开播放，极速加载流畅观影，海量片库一网打尽。',
            url: 'https://www.dashixiong.tv/',
            isPartOf: {
              '@type': 'WebSite',
              name: '大师兄影视',
              url: 'https://www.dashixiong.tv/',
            },
          }),
        }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[420px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
          {heroBackgrounds.map((bg, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-1000"
              style={{ opacity: index === currentBg ? 1 : 0 }}
            >
              <img
                src={bg}
                alt="大师兄影视背景"
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/40 to-background-50"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-100 font-heading leading-tight">
                <span className="text-primary-400">大师兄影视</span>
              </h1>
              <p className="text-base md:text-lg text-foreground-300 mt-4 max-w-xl mx-auto leading-relaxed">
                汇聚电视剧全集 · 电影大全 · 热门综艺<br />
                高清免费在线观看，不缓冲秒开播放
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <div className="relative w-full max-w-md">
                  <input
                    type="text"
                    placeholder="搜索你喜欢的影视内容..."
                    className="w-full px-5 py-3.5 pl-11 text-sm bg-background-100/80 backdrop-blur-md border border-background-300 rounded-full text-foreground-200 placeholder-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-base"></i>
                </div>
                <Link
                  to="/category/tv-series"
                  className="px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即观看
                </Link>
              </div>

              <div className="flex items-center justify-center gap-1.5 mt-6">
                {heroBackgrounds.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBg(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentBg ? 'bg-primary-500 w-6' : 'bg-foreground-500/40 hover:bg-foreground-500/60'
                    }`}
                    aria-label={`切换到背景 ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-12">
          <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 px-4 py-2.5 bg-background-100 hover:bg-background-200 border border-background-200 hover:border-primary-500/30 rounded-full text-sm text-foreground-300 hover:text-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Hot Movies */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <SectionHeader
            title="🔥 热播推荐"
            subtitle="全网最热影视，大家都在看"
            linkTo="/category/tv-series"
            linkText="查看更多"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {hotMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* New Movies */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-100/50">
          <SectionHeader
            title="✨ 最新上线"
            subtitle="新鲜出炉，第一时间观看"
            linkTo="/category/movies"
            linkText="查看更多"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {newMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* Classic Movies */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <SectionHeader
            title="🏆 经典影片"
            subtitle="口碑佳作，百看不厌"
            linkTo="/category/movies"
            linkText="查看更多"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {classicMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20banner%20with%20film%20strip%20and%20glowing%20particles%2C%20dark%20luxurious%20background%2C%20warm%20golden%20light%20accents%2C%20movie%20marathon%20atmosphere%2C%20elegant%20and%20premium%20feel&width=1600&height=400&seq=cta-banner&orientation=landscape"
              alt="畅享影视盛宴"
              className="w-full h-[200px] md:h-[280px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-transparent flex items-center">
              <div className="px-8 md:px-14 py-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">随时随地，畅享影视盛宴</h2>
                <p className="text-sm md:text-base text-foreground-300 mt-3 max-w-md">高清画质 · 秒开播放 · 海量片库 · 完全免费</p>
                <Link
                  to="/category/tv-series"
                  className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始探索
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}